import { signupMarketingContext } from "./marketingTracking";
import type { Post } from "../models/Post";

type User = { getIdToken(): Promise<string> };
export function metaRequestContext() {
  return process.env.REACT_APP_META_TRACKING_ENABLED === "true"
    ? signupMarketingContext() ?? null
    : null;
}

/** Separate best-effort request. Never await this in a product action. */
async function sendIntent(
  user: User,
  workspaceId: string,
  name: string,
  sourceId: string,
  mode?: string,
) {
  if (process.env.REACT_APP_META_TRACKING_ENABLED !== "true") return;
  if (name !== "InitiateCheckout" && !metaRequestContext()) return;
  try {
    const token = await user.getIdToken();
    const marketing = metaRequestContext();
    if (name !== "InitiateCheckout" && !marketing) return;
    await fetch(`${process.env.REACT_APP_API_BASE}/api/meta/intent`, {
      method: "POST",
      keepalive: true,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "x-workspace-id": workspaceId,
      },
      body: JSON.stringify({
        name,
        sourceId,
        marketing,
        ...(mode ? { mode } : {}),
      }),
      signal: AbortSignal.timeout(5000),
    });
  } catch {
    /* Missing an intent signal must never block the user. */
  }
}

const seen = new Set<string>();
function firstInBrowser(key: string): boolean {
  if (seen.has(key)) return false;
  try {
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, "1");
  } catch {
    /* Session memory still deduplicates. */
  }
  seen.add(key);
  return true;
}
export async function trackUpgradeView(user: User, workspaceId: string) {
  if (!metaRequestContext()) return;
  const bucket = String(Math.floor(Date.now() / 1800_000));
  // Session memory only: avoid accumulating one localStorage key per time window.
  const key = `pricing:${workspaceId}:${bucket}`;
  if (seen.has(key)) return;
  seen.add(key);
  await sendIntent(user, workspaceId, "PricingViewed", bucket);
}
export async function trackCheckoutStarted(
  user: User,
  workspaceId: string,
  sessionId: string,
) {
  // Also sends null context to clear the current checkout's matching metadata.
  // Server IDs deduplicate repeat opens of the same Stripe session.
  await sendIntent(user, workspaceId, "InitiateCheckout", sessionId);
}
export async function trackPublishAttempt(
  user: User,
  workspaceId: string,
  post: Partial<Post> | null | undefined,
  mode: "now" | "schedule",
) {
  if (!metaRequestContext()) return;
  const historical =
    ["scheduled", "publishing", "published", "mixed"].includes(
      post?.status || "",
    ) ||
    Object.values(post?.connections || {}).some(
      (c) =>
        !!(
          c.taskName ||
          c.deferred ||
          c.publishedAt ||
          c.publishAttemptStartedAt
        ) || ["failed", "published", "publishing"].includes(c.status),
    );
  const first = firstInBrowser(`wg:first-publish:${workspaceId}`);
  if (!historical && first)
    await sendIntent(user, workspaceId, "FirstPublishAttempt", "first", mode);
}
